import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { router } from "./router";
import "./styles.css";
import { i18n as _bootI18n } from "./lib/i18n";
// Reference the export so Vite cannot relocate the i18n init module into a
// lazily-loaded route chunk. Without this, the init call (and bundled locale
// JSON) end up in the Profile chunk and every other screen renders raw keys
// like `my_uploads.title` until Profile is visited. OTA-safe.
void _bootI18n.language;
import { initAppLifecycle } from "./lib/native/app-lifecycle";
import { installUploadDrainListener } from "./lib/native/upload-drain";
import { installStrictUploadDrainHandler } from "./lib/native/upload-drain-handler";
import { startActiveWatch } from "./lib/native/active-watch";
import { getTrackingState } from "./lib/native/tracking-state";
import {
  markBootAttempt,
  checkVersionAndMaybeReload,
  installBootWatchdog,
} from "./lib/boot-recovery";
import { hydrateCachedTrip } from "./lib/offline/trip-cache";
import { hydrateLocalUploadRules } from "./lib/offline/upload-rules-local";
import { initReachability } from "./lib/connectivity/reachability";
import { supabase } from "./integrations/supabase/client";
import { clearLastRoute } from "./lib/last-route";

// Clear the saved last-route on sign-out so a different user doesn't
// inherit the previous user's last screen on next sign-in.
supabase.auth.onAuthStateChange((event) => {
  if (event === "SIGNED_OUT") clearLastRoute();
});


// OTA-safe cold-launch recovery. Must run BEFORE anything that could throw
// during module init so the watchdog can recover from a stale WebView.
markBootAttempt();
checkVersionAndMaybeReload();
installBootWatchdog(8000);

// Defer non-critical work until the main thread is idle. Failures are
// swallowed so a flaky deferred task can't crash boot.
const runWhenIdle = (fn: () => void) => {
  const safe = () => {
    try { fn(); } catch (e) { console.warn("[boot] deferred task failed", e); }
  };
  if (typeof window === "undefined") return;
  const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number };
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(safe, { timeout: 3000 });
  } else {
    window.setTimeout(safe, 1500);
  }
};

// Native init: status bar + splash hide.
// The iOS wrapper now loads the hosted site at https://roava.lovable.app
// (see capacitor.config.json `server.url`), so every Lovable publish is
// instantly live in the App Store binary — no Capgo bundle dance, no
// resubmission for UI changes.
import { Capacitor } from "@capacitor/core";
if (Capacitor.isNativePlatform()) {
  import("@capacitor/status-bar")
    .then(({ StatusBar, Style }) => {
      StatusBar.setStyle({ style: Style.Light }).catch(() => {});
    })
    .catch(() => {});
  // Kill the launch logo flash the moment React mounts.
  import("@capacitor/splash-screen")
    .then(({ SplashScreen }) => {
      SplashScreen.hide({ fadeOutDuration: 0 }).catch(() => {});
    })
    .catch(() => {});
  // Optional "Support Roava" tips — consumable Apple IAP only. Deferred so
  // it doesn't block first paint on cold start.
  runWhenIdle(() => {
    import("./lib/native/iap")
      .then(({ initIap }) => { void initIap(); })
      .catch(() => {});
  });
}

// Dev-only: warn if any <img> in the native shell still points at a Lovable
// CDN URL (it would render as a broken-image placeholder on iOS).
if (import.meta.env.DEV && Capacitor.isNativePlatform()) {
  void import("./lib/native/warn-cdn-image-on-native").then(({ installNativeCdnImageWarning }) => {
    installNativeCdnImageWarning();
  });
}

// App-lifecycle + upload-drain listeners — eager: required for offline/upload
// safety before the user can interact with any tracking or upload screen.
initAppLifecycle();
installUploadDrainListener();
installStrictUploadDrainHandler();
initReachability();

// Outbox-guard: log queued ping count across auth events so we'd catch a
// future regression where sign-in destroys the IndexedDB outbox. Pure
// observability — no retry/repair logic.
void (async () => {
  try {
    const [{ supabase }, { getOutboxStats }] = await Promise.all([
      import("./integrations/supabase/client"),
      import("./lib/native/ping-outbox"),
    ]);
    supabase.auth.onAuthStateChange((event) => {
      const before = getOutboxStats().queued;
      setTimeout(() => {
        const after = getOutboxStats().queued;
        const tag = after < before ? "[outbox-guard][WARN]" : "[outbox-guard]";
        // eslint-disable-next-line no-console
        console.info(`${tag} auth event=${event} queued=${before}→${after}`);
      }, 250);
    });
  } catch { /* ignore */ }
})();

// OTA-safe tracking reliability controller. Self-heals the active watch,
// drains the native LocationBG queue, then drains the JS ping outbox on
// boot, app resume, network reconnect, tracking-on, and a 90s interval
// while visible. Idempotent + single-flight — no duplicate pings.
void import("./lib/native/tracking-reliability").then(({ installTrackingReliability }) => {
  installTrackingReliability();
});

// OTA-safe tracking health watcher: while a trip is recording, fire a local
// notification when no GPS ping has been recorded for >60min so the traveler
// can fix permissions/Low Power Mode mid-trip instead of finding gaps later.
void import("./lib/tracking/health-watcher").then(({ installTrackingHealthWatcher }) => {
  installTrackingHealthWatcher();
});

// Offline-first: hydrate local caches early so the UI never blocks on the
// first KV read.
void hydrateCachedTrip();
void hydrateLocalUploadRules();

// Non-critical: settings reconcile, timezone monitor, and stale-build watcher.
// None of these are needed for first paint or first interaction.
runWhenIdle(() => {
  void import("./lib/offline/settings-sync").then(({ installSettingsSync }) => {
    installSettingsSync();
  });
});
runWhenIdle(() => {
  void import("./lib/time/timezone-monitor").then(({ initTimezoneMonitor }) => {
    initTimezoneMonitor();
  });
});
runWhenIdle(() => {
  void import("./lib/stale-build").then(({ startStaleBuildWatcher }) => {
    startStaleBuildWatcher();
  });
});
// iOS background URLSession reconciliation. No-op on web and on native
// builds that don't bundle the BackgroundUploader plugin.
if (Capacitor.isNativePlatform()) {
  runWhenIdle(() => {
    void import("./lib/native/background-uploader").then(({ reconcileBackgroundUploads }) => {
      void reconcileBackgroundUploads();
    });
  });
}


async function resumeTrackingOnColdStart() {
  try {
    const wasOn = await getTrackingState();
    if (!wasOn) {
      // Not tracking on cold-start — clear any orphan Live Activities left
      // over from a previous force-quit.
      const { endOrphanLiveActivitiesIfSafe } = await import("./lib/native/live-activity");
      void endOrphanLiveActivitiesIfSafe({ source: "main-cold-start" });
      return;
    }
    // We don't know the tripId at boot — load the user's current trip lazily.
    const { getMyTrip } = await import("./lib/trip.functions");
    const trip = await getMyTrip({});
    const tripId = trip?.trip?.id;
    if (tripId) await startActiveWatch(tripId);
  } catch (e) {
    // Best-effort only — the Tracking screen will retry when opened.
    console.warn("[boot] resume tracking failed", e);
  }
}

const rootEl = document.getElementById("root")!;

// Gate first paint behind a short /version.json check on the WEB only — a
// stale cached HTML bundle could otherwise visibly render before the
// cache-busting redirect kicks in. On native the RoavaShell already probed
// roava.lovable.app and only navigated us here once the remote was
// reachable, so this extra fetch is pure latency (saves up to 400 ms per
// cold launch). The post-mount stale-build watcher still catches any
// version drift after the app boots.
if (Capacitor.isNativePlatform()) {
  createRoot(rootEl).render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  );
} else {
  void import("./lib/stale-build").then(async ({ gateFirstPaintOnVersion }) => {
    const redirecting = await gateFirstPaintOnVersion(1000);
    if (redirecting) return;
    createRoot(rootEl).render(
      <StrictMode>
        <RouterProvider router={router} />
      </StrictMode>,
    );
  });
}

// Resume background GPS after first paint so auth + navigation stay snappy on
// cold launch. The route singleton keeps the WatchHandle alive across
// component unmounts; if no trip is loaded yet, the Tracking screen will pick
// it up on mount.
if (Capacitor.isNativePlatform()) {
  runWhenIdle(() => {
    void resumeTrackingOnColdStart();
  });
}


